import { useState } from "react";
import {
    DialogContent,
    Dialog,
    Divider,
    DialogTitle,
    Box,
    Popover,
    MenuItem,
} from "@mui/material";
import { CloseSvg } from "@convin/components/svg";
import { matchPath, useLocation } from "react-router-dom";
import LearnMoreSvg from "@convin/components/svg/LearnMoreSvg";
import { isDefined } from "@convin/utils/helper/common.helper";
import {
    DashboardRoutesConfig,
    SettingRoutes,
} from "@convin/config/routes.config";
import { Label } from "@convin/components/custom_components/Typography/Label";
import MediaPlayer from "@convin/modules/conversationDetails/components/MediaPlayer";
import MediaPlayerContextProvider from "@convin/modules/conversationDetails/context/MediaPlayerContextProvider";
import useBranding from "@convin/hooks/useBranding";
import { moduleKeys } from "@convin/type/LearnMore";
import DownArrowSvg from "@convin/components/svg/DownArrowSvg";

const { CI, Account, Home, LMS, Conversations } = DashboardRoutesConfig;

const record: Record<
    string | moduleKeys,
    {
        link: string;
        label: string;
    }
> = {
    [`${CI.subRoutes.ai_insights.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/0ae6e5e9-3303-4598-b7bd-180b1a379d47.mp4",
        label: "AI Insights",
    },
    [`${CI.subRoutes.insights.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/3e23010e-b1de-4a86-a203-498727d849d9.mp4",
        label: "Customer Intelligence",
    },
    [`${CI.subRoutes.custom_tracking.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/91a0d42b-c591-4250-aa7f-88a7ba91ae95.mp4",
        label: "Custom Tracking",
    },
    [`${SettingRoutes["AUDIT_MANAGER"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/61d070c7-4c8e-44ee-bbee-23c147aed22a.mp4",
        label: "Introduction to Audit Manager",
    },
    [`${SettingRoutes["VIOLATION_MANAGER"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/69645293-1e03-421d-ba20-7740cf86de66.mp4",
        label: "Setting Up Violations & Marking Rule ",
    },
    create_template: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/88accfb6-8d23-4e21-88ee-3af2e6cd278d.mp4",
        label: "Creating an Audit Template",
    },
    create_category_parameter: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/88accfb6-8d23-4e21-88ee-3af2e6cd278d.mp4",
        label: "Creation of Categories and Parameters",
    },
    account_level_configuration: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/bb414018-ec37-4571-b648-a05561c867fa.mp4",
        label: "Account level setting in Parameter",
    },
    share_via_email: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/aafe143d-a35e-4b4d-8167-e1f136ba17f0.mp4",
        label: "Sharing Call List via Email",
    },
    [`${SettingRoutes["SCHEDULED_REPORTS"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/8c18a2d3-57ac-4022-b0a8-bb4f221875fe.mp4",
        label: "Scheduled Reports",
    },
    [`${SettingRoutes["USER_MANAGER"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/73a8bf17-b1fb-4153-be64-ce8dae7e2a6e.mp4",
        label: "User manager",
    },
    [`${SettingRoutes["SAMPLING_MANAGER"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/d7c743c6-4c0d-4f24-ae3b-118bc4c93337.mp4",
        label: "Sampling Manager - intro",
    },
    bulk_upload_users: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/73a8bf17-b1fb-4153-be64-ce8dae7e2a6e.mp4",
        label: "User manager - Bulk Upload",
    },
    create_sampling_rule: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/e1b71c95-258a-4ed5-96f6-efe6d76ef46a.mp4",
        label: "Sampling Manager -  AI/manual Accounts",
    },
    [`${SettingRoutes["MANUAL_QMS"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/75a83f88-80a8-4736-bdb2-bb4437d3d5de.mp4",
        label: "Manual QMS settings",
    },
    [`${SettingRoutes["ENTITY"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/da94e6d9-1aed-453f-b1fe-63e7d2455924.mp4",
        label: "How to create new entity",
    },
    Lead: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/90687228-8346-4f56-8b1a-1d1c8b586142.mp4",
        label: "How to configure Lead Interest",
    },
    CSAT: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/90e6ac5c-c477-445e-9614-4943237aaf77.mp4",
        label: "How to configure CSAT Analysis",
    },
    Collection: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/4abf56c5-1b43-4ee1-8d05-65e4ee488ce2.mp4",
        label: "How to configure Collection Analysis",
    },
    [`${SettingRoutes["AI_INSIGHTS"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/da94e6d9-1aed-453f-b1fe-63e7d2455924.mp4",
        label: "How to create custom insight",
    },
    [`${SettingRoutes["AI_DISPOSITION"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/6861c322-ae9a-429e-a34f-e9825723e2aa.mp4",
        label: "How to create AI disposition",
    },
    [`${SettingRoutes["AI_SUMMARY"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/da1aff7e-3460-49f5-90a0-ae0d5be05732.mp4",
        label: "How to create new custom summary ",
    },
    [`${SettingRoutes["ROLE_MANAGER"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/418201a0-5030-442f-8a86-c6e318d4cf98.mp4",
        label: "Role Manager Settings",
    },
    [`${SettingRoutes["TEAM_MANAGER"].path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/920f8ff0-091c-47f0-a2bc-97a368dd1852.mp4",
        label: "Team Manager Settings",
    },
    account_audit: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/9eff981b-a52e-4567-8ff7-1cc25752c947.mp4",
        label: "How to perform account level audit",
    },
    [`${Account.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/5f2a9e62-61ff-454b-a550-51e2d63c6003.mp4",
        label: "Explanation basic Account level View - list page",
    },
    [`${Conversations.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/924a0907-65d9-4b59-acd2-fd566b3514ea.mp4",
        label: "Conversation List Page Overview",
    },

    account_details: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/b8960f0c-79fd-45a2-bb0b-70ac8f9428d4.mp4",
        label: "Explanation of account details page",
    },
    acknowledge_dispute: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/42f02e85-4e2b-4e3f-b70c-f82311690106.mp4",
        label: "Acknowledgement and Dispute",
    },
    [`${Home.subRoutes.team_level_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/41756d12-86c0-4dee-8a98-5c87da86e605.mp4",
        label: "Team level Analysis",
    },
    [`${Home.subRoutes.rep_level_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/931984c3-1bdc-49a8-853b-484de52f5494.mp4",
        label: "Rep level Dashboard",
    },
    [`${Home.subRoutes.parameter_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/a5233529-f7d0-4814-a237-368cffacb251.mp4",
        label: "Parameter Dashboard Overview",
    },
    [`${Home.subRoutes.violation_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/2297fef1-c43a-489c-bac0-2fa7fe5fbe0f.mp4",
        label: "Violation Dashboard Overview",
    },
    [`${Home.subRoutes.audit_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/a8880b2c-8270-4bb6-9472-b5fa858e7e79.mp4",
        label: "Audit Dashboard Overview",
    },
    [`${Home.subRoutes.accuracy_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/16c69a08-3209-4149-8f2c-785e03938e0d.mp4",
        label: "Accuracy Dashboard Overview",
    },
    [`${Home.subRoutes.collection_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/5544c379-a8c8-408f-ae57-c9994190cc18.mp4",
        label: "Collection Dashboard Overview",
    },
    [`${Home.subRoutes.lead_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/87cc74e4-2087-4e8d-a587-1206ef2b524d.mp4",
        label: "Lead Interest Dashboard Overview",
    },
    [`${Home.subRoutes.csat_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/6ecd50c5-224b-4446-aedc-29b6615b784b.mp4",
        label: "CSAT Dashboard Overview",
    },
    [`${Home.subRoutes.overall_dashboard.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/12f2524f-cda6-4a0e-bc67-d16bc1039d5f.mp4",
        label: "Overall Dashboard Overview",
    },
    [`${LMS.subRoutes.knowledgeBase.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/612e3821-230d-492e-a732-0b96e5c694b1.mp4",
        label: "Knowledge Base Overview",
    },
    [`${LMS.subRoutes.assesments.path}`]: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/dad11707-255e-4317-bd4e-1cefc1acbb4c.mp4",
        label: "Assessment Form",
    },
    question_bank: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/f3c2313d-0160-4375-9939-36aaa542ebda.mp4",
        label: "Question Bank",
    },
    create_group_block: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/0aa09b8c-9523-4c1f-ac9a-f8843235ee38.mp4",
        label: "Creating a Group Block",
    },
    create_combination_block: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/a5c7e625-c2e3-4ec6-9d50-74ab8a04c905.mp4",
        label: "Creating a Combination Block",
    },
    share_conversation: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/6d1000a0-7996-4802-89f2-8bbce504509d.mp4",
        label: "Sharing Conversation Snippets",
    },
    single_response: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/29678021-c92f-4042-8945-06616e762d9d.mp4",
        label: "Single Response",
    },
    add_to_lms: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/1015425b-2b33-4fbf-9a87-7c6f886e8b99.mp4",
        label: "Add to LMS Feature Guide",
    },
    conversation_detail: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/dbbdbbc4-0cfa-431a-9c44-2c7ecf8b01d1.mp4",
        label: "Conversation Details Page Overview",
    },
    manual_call_audit_process: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/0af6d58a-8631-467c-88c3-79dc2516f869.mp4",
        label: "Manual Call Audit Process Overview",
    },
    audit_type: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/d91976d8-42dc-4c98-a925-8657abf3b3cf.mp4",
        label: "Audit Type Overview",
    },
    multiple_response: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/a8d8e1d9-d563-4b14-ae57-5547e84127b7.mp4",
        label: "Multiple Response",
    },
    llm_audit: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/8f346d3e-36a2-4028-a23c-f4980ec38eea.mp4",
        label: "Configuring Parameters via Convin LLM",
    },
    alert: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/4511b215-81f8-4f64-bf67-c531c4514fd9.mp4",
        label: "Alert Feature Overview",
    },
    qms_audit_process: {
        link: "https://s3.us-east-1.amazonaws.com/public-access-convin.ai/product_demo_videos/a93149be-20ad-4846-b253-be19e8bbe08b.mp4",
        label: "QMS Audit Process",
    },
};

export const LearnMore: React.FC<{
    module?: moduleKeys;
    type?: "label_icon" | "icon";
    is_menu?: boolean;
    menuOptions?: { label: string; module: moduleKeys }[];
}> = ({ module, type = "icon", is_menu = false, menuOptions = [] }) => {
    const { branding } = useBranding();

    const [open, setOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedModule, setSelectedModule] = useState<
        moduleKeys | undefined
    >(module);
    const { pathname } = useLocation();
    const route = matchPath<"dashboard_type", string>(
        "/:dashboard_type/*",
        pathname
    );
    const subRoute = matchPath<"subroute", string>(
        "/:dashboard_type/:subroute/*",
        pathname
    );
    if (
        !isDefined(route?.params?.dashboard_type) &&
        !isDefined(subRoute?.params?.subroute) &&
        !isDefined(module) &&
        !is_menu
    )
        return null;

    const object = isDefined(selectedModule)
        ? record[selectedModule as keyof typeof record]
        : isDefined(subRoute?.params?.subroute)
        ? record[subRoute!.params.subroute as keyof typeof record]
        : isDefined(route?.params?.dashboard_type)
        ? record[route!.params.dashboard_type as keyof typeof record]
        : null;

    if (branding !== "Convin") return null;

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    // Popover close handler
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Menu item click handler
    const handleOptionClick = (mod: moduleKeys) => {
        setSelectedModule(mod);
        setAnchorEl(null);
        setOpen(true);
    };
    return (
        <>
            {is_menu ? (
                <>
                    <Box
                        type="button"
                        onClick={handleMenuClick}
                        sx={{
                            color:
                                type === "label_icon"
                                    ? "primary.main"
                                    : "textColors.666",
                        }}
                        component={"button"}
                        className="inline-flex items-center gap-1"
                        role="button"
                    >
                        <LearnMoreSvg />{" "}
                        {type === "label_icon" ? "Learn More" : ""}
                        <DownArrowSvg />
                    </Box>
                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                    >
                        {menuOptions.map((opt) => (
                            <MenuItem
                                key={opt.module}
                                onClick={() => handleOptionClick(opt.module)}
                            >
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Popover>
                </>
            ) : (
                <Box
                    type="button"
                    onClick={() => setOpen(true)}
                    sx={{
                        color:
                            type === "label_icon"
                                ? "primary.main"
                                : "textColors.666",
                    }}
                    component={"button"}
                    className="inline-flex items-center gap-1"
                    role="button"
                >
                    <LearnMoreSvg /> {type === "label_icon" ? "Learn More" : ""}
                </Box>
            )}
            {object && (
                <Dialog
                    open={open}
                    sx={{
                        "& .MuiDialog-paper": {
                            borderRadius: "12px",
                            width: "700px",
                            maxWidth: "700px",
                        },
                        "& .MuiDialogContent-root": {
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        },
                        "& .react-player-container": {
                            width: "100%",
                            height: "100%",
                            borderRadius: "0px !important",
                        },
                    }}
                >
                    <DialogTitle
                        className="flex items-center justify-between"
                        sx={{ p: 1, gap: 2 }}
                    >
                        <Label
                            colorType="333"
                            hasTooltip
                            isEllipses
                            variant="large"
                            className="font-semibold"
                        >
                            {object.label}
                        </Label>
                        <button onClick={() => setOpen(false)}>
                            <CloseSvg />
                        </button>
                    </DialogTitle>
                    <Divider />

                    <DialogContent
                        sx={{
                            p: 0,
                            "& #header-bar": {
                                display: "none",
                            },
                            "& .react-player--download__button": {
                                display: "none",
                            },
                            "& .expand-player-button": {
                                display: "none",
                            },
                        }}
                    >
                        <MediaPlayerContextProvider>
                            <MediaPlayer
                                mediaUrl={object.link}
                                height={352}
                                isSnippet
                            />
                        </MediaPlayerContextProvider>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};
